import React, { useCallback, useEffect, useState } from 'react';
import {
    Badge,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Pagination,
    Placeholder,
    Row,
    Tab,
    Table,
    Tabs,
} from 'react-bootstrap';
import { BsClipboard, BsPencil, BsPlayCircle, BsTrash3 } from 'react-icons/bs';
import { TbPlugConnectedX } from 'react-icons/tb';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import MainContainer from '../../../MainContainer';
import { baseURL } from '../../../../helpers/baseURL';
import { getRequestErrorMessage, headers } from '../../../../helpers/Requests';
import successAlert from '../../../../helpers/successAlert';
import failAlert from '../../../../helpers/failAlert';
import keywordBadgeStyle from '../../../../helpers/keywordBadgeStyle';
import { formatDateTime } from '../../../../helpers/formatDate';
import { showConfirmDialog } from '../../../ItemDelete';
import { EmbedFormsActiveSelect } from '../../embedForms/embedFormsActiveSelect';
import EmbedListSortableTh from '../../embedForms/embedList/EmbedListSortableTh';
import tableStyles from '../../tickets/TicketsTable.module.css';
import plus from '../../../../../css/img/plus.svg';
import AutoTransferRuleModal from './AutoTransferRuleModal';
import AutoTransferTestModal from './AutoTransferTestModal';
import AutoTransferLogModal from './AutoTransferLogModal';
import '../../embedForms/embed-form-editor.css';

const DEFAULT_SORT = { by: 'created_at', direction: 'desc' };
const DEFAULT_PAGINATION_META = {
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
};
const TAB_KEYS = ['generated', 'connections', 'notifications', 'auto-transfer'];

const getTabFromSearch = (search) => {
    const tab = new URLSearchParams(search).get('tab');
    return TAB_KEYS.includes(tab) ? tab : 'generated';
};

const nextSortState = (prev, columnKey) => ({
    by: columnKey,
    direction:
        columnKey === 'is_active' || columnKey === 'code_is_active'
            ? prev.by === columnKey && prev.direction === 'desc'
                ? 'asc'
                : 'desc'
            : prev.by === columnKey && prev.direction === 'asc'
              ? 'desc'
              : 'asc',
});

const buildListParams = (page, perPage, sort) => ({
    page,
    per_page: perPage,
    sort_by: sort.by,
    sort_direction: sort.direction,
});

const getPageNumbers = (currentPage, totalPages, onPageChange) => {
    const pages = [];
    const maxVisible = 16;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = start + maxVisible - 1;
    if (end > totalPages) {
        end = totalPages;
        start = Math.max(1, end - maxVisible + 1);
    }
    if (start > 1) {
        pages.push(
            <Pagination.Item key={1} onClick={() => onPageChange(1)}>
                1
            </Pagination.Item>
        );
        if (start > 2) {
            pages.push(<Pagination.Ellipsis key="start-ellipsis" />);
        }
    }
    for (let i = start; i <= end; i++) {
        pages.push(
            <Pagination.Item key={i} active={i === currentPage} onClick={() => onPageChange(i)}>
                {i}
            </Pagination.Item>
        );
    }
    if (end < totalPages) {
        if (end < totalPages - 1) {
            pages.push(<Pagination.Ellipsis key="end-ellipsis" />);
        }
        pages.push(
            <Pagination.Item key={totalPages} onClick={() => onPageChange(totalPages)}>
                {totalPages}
            </Pagination.Item>
        );
    }
    return pages;
};

const TransferConnections = () => {
    const history = useHistory();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));
    const [codes, setCodes] = useState([]);
    const [connected, setConnected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectionsLoading, setConnectionsLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [label, setLabel] = useState('');
    const [createError, setCreateError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [editLabel, setEditLabel] = useState('');
    const [editError, setEditError] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [connectUuid, setConnectUuid] = useState('');
    const [connecting, setConnecting] = useState(false);
    const [connectError, setConnectError] = useState('');
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [updatingActiveId, setUpdatingActiveId] = useState(null);
    const [disconnectingId, setDisconnectingId] = useState(null);
    const [deauthorizingId, setDeauthorizingId] = useState(null);
    const [notifyEmails, setNotifyEmails] = useState([]);
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [notifyEmailInput, setNotifyEmailInput] = useState('');
    const [notifySaving, setNotifySaving] = useState(false);
    const [notifyRemovingId, setNotifyRemovingId] = useState(null);
    const [codesSort, setCodesSort] = useState(DEFAULT_SORT);
    const [connectedSort, setConnectedSort] = useState({ by: 'connected_at', direction: 'desc' });
    const [notifySort, setNotifySort] = useState(DEFAULT_SORT);
    const [codesPage, setCodesPage] = useState(1);
    const [codesPerPage, setCodesPerPage] = useState(10);
    const [codesMeta, setCodesMeta] = useState(DEFAULT_PAGINATION_META);
    const [connectedPage, setConnectedPage] = useState(1);
    const [connectedPerPage, setConnectedPerPage] = useState(10);
    const [connectedMeta, setConnectedMeta] = useState(DEFAULT_PAGINATION_META);
    const [notifyPage, setNotifyPage] = useState(1);
    const [notifyPerPage, setNotifyPerPage] = useState(10);
    const [notifyMeta, setNotifyMeta] = useState(DEFAULT_PAGINATION_META);
    const [autoRules, setAutoRules] = useState([]);
    const [autoRulesLoading, setAutoRulesLoading] = useState(false);
    const [autoRulesSort, setAutoRulesSort] = useState({ by: 'name', direction: 'asc' });
    const [autoRulesPage, setAutoRulesPage] = useState(1);
    const [autoRulesPerPage, setAutoRulesPerPage] = useState(10);
    const [autoRulesMeta, setAutoRulesMeta] = useState(DEFAULT_PAGINATION_META);
    const [showRuleModal, setShowRuleModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [togglingRuleId, setTogglingRuleId] = useState(null);
    const [testingRule, setTestingRule] = useState(null);
    const [showAutoLogs, setShowAutoLogs] = useState(false);
    const [autoLogs, setAutoLogs] = useState([]);
    const [autoLogsLoading, setAutoLogsLoading] = useState(false);
    const [autoLogsPage, setAutoLogsPage] = useState(1);
    const [autoLogsPerPage, setAutoLogsPerPage] = useState(10);
    const [autoLogsMeta, setAutoLogsMeta] = useState(DEFAULT_PAGINATION_META);
    const [selectedLog, setSelectedLog] = useState(null);

    const selectTab = useCallback((key) => {
        const tab = TAB_KEYS.includes(key) ? key : 'generated';
        setActiveTab(tab);
        const params = new URLSearchParams(location.search);
        if (tab === 'generated') {
            params.delete('tab');
        } else {
            params.set('tab', tab);
        }
        const search = params.toString();
        history.replace({
            pathname: location.pathname,
            search: search ? `?${search}` : '',
        });
    }, [history, location.pathname, location.search]);

    useEffect(() => {
        const tabFromUrl = getTabFromSearch(location.search);
        setActiveTab((prev) => (prev === tabFromUrl ? prev : tabFromUrl));
    }, [location.search]);

    const loadCodes = useCallback(async (pageNum = codesPage, pageSize = codesPerPage, sortSnapshot = codesSort) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${baseURL}ticket-transfer-codes`, {
                ...headers,
                params: buildListParams(pageNum, pageSize, sortSnapshot),
            });
            setCodes(Array.isArray(data?.codes) ? data.codes : []);
            setCodesMeta({
                total: data?.total ?? 0,
                per_page: data?.per_page ?? pageSize,
                current_page: data?.current_page ?? pageNum,
                last_page: data?.last_page ?? 1,
            });
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load transfering codes.');
            setCodes([]);
        } finally {
            setLoading(false);
        }
    }, [codesPage, codesPerPage, codesSort]);

    const loadConnections = useCallback(async (pageNum = connectedPage, pageSize = connectedPerPage, sortSnapshot = connectedSort) => {
        setConnectionsLoading(true);
        try {
            const { data } = await axios.get(`${baseURL}ticket-transfer-connections`, {
                ...headers,
                params: buildListParams(pageNum, pageSize, sortSnapshot),
            });
            setConnected(Array.isArray(data?.connected) ? data.connected : []);
            setConnectedMeta({
                total: data?.total ?? 0,
                per_page: data?.per_page ?? pageSize,
                current_page: data?.current_page ?? pageNum,
                last_page: data?.last_page ?? 1,
            });
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load connections.');
            setConnected([]);
        } finally {
            setConnectionsLoading(false);
        }
    }, [connectedPage, connectedPerPage, connectedSort]);

    const loadNotifyEmails = useCallback(async (pageNum = notifyPage, pageSize = notifyPerPage, sortSnapshot = notifySort) => {
        setNotifyLoading(true);
        try {
            const { data } = await axios.get(`${baseURL}ticket-transfer-notification-emails`, {
                ...headers,
                params: buildListParams(pageNum, pageSize, sortSnapshot),
            });
            setNotifyEmails(Array.isArray(data?.emails) ? data.emails : []);
            setNotifyMeta({
                total: data?.total ?? 0,
                per_page: data?.per_page ?? pageSize,
                current_page: data?.current_page ?? pageNum,
                last_page: data?.last_page ?? 1,
            });
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load notification emails.');
            setNotifyEmails([]);
        } finally {
            setNotifyLoading(false);
        }
    }, [notifyPage, notifyPerPage, notifySort]);

    const loadAutoRules = useCallback(async (pageNum = autoRulesPage, pageSize = autoRulesPerPage, sortSnapshot = autoRulesSort) => {
        setAutoRulesLoading(true);
        try {
            const { data } = await axios.get(`${baseURL}auto-transfer-rules`, {
                ...headers,
                params: buildListParams(pageNum, pageSize, sortSnapshot),
            });
            setAutoRules(Array.isArray(data?.rules) ? data.rules : []);
            setAutoRulesMeta({
                total: data?.total ?? 0,
                per_page: data?.per_page ?? pageSize,
                current_page: data?.current_page ?? pageNum,
                last_page: data?.last_page ?? 1,
            });
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load auto-transfer rules.');
            setAutoRules([]);
        } finally {
            setAutoRulesLoading(false);
        }
    }, [autoRulesPage, autoRulesPerPage, autoRulesSort]);

    const loadAutoLogs = useCallback(async (pageNum = autoLogsPage, pageSize = autoLogsPerPage) => {
        setAutoLogsLoading(true);
        try {
            const { data } = await axios.get(`${baseURL}auto-transfer-logs`, {
                ...headers,
                params: { page: pageNum, per_page: pageSize },
            });
            setAutoLogs(Array.isArray(data?.logs) ? data.logs : []);
            setAutoLogsMeta({
                total: data?.total ?? 0,
                per_page: data?.per_page ?? pageSize,
                current_page: data?.current_page ?? pageNum,
                last_page: data?.last_page ?? 1,
            });
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to load auto-transfer logs.');
            setAutoLogs([]);
        } finally {
            setAutoLogsLoading(false);
        }
    }, [autoLogsPage, autoLogsPerPage]);

    useEffect(() => {
        if (activeTab === 'generated') {
            loadCodes(codesPage, codesPerPage, codesSort);
        }
    }, [activeTab, codesPage, codesPerPage, codesSort]);

    useEffect(() => {
        if (activeTab === 'connections') {
            loadConnections(connectedPage, connectedPerPage, connectedSort);
        }
    }, [activeTab, connectedPage, connectedPerPage, connectedSort]);

    useEffect(() => {
        if (activeTab === 'notifications') {
            loadNotifyEmails(notifyPage, notifyPerPage, notifySort);
        }
    }, [activeTab, notifyPage, notifyPerPage, notifySort]);

    useEffect(() => {
        if (activeTab === 'auto-transfer') {
            loadAutoRules(autoRulesPage, autoRulesPerPage, autoRulesSort);
        }
    }, [activeTab, autoRulesPage, autoRulesPerPage, autoRulesSort]);

    useEffect(() => {
        if (activeTab === 'auto-transfer' && showAutoLogs) {
            loadAutoLogs(autoLogsPage, autoLogsPerPage);
        }
    }, [activeTab, showAutoLogs, autoLogsPage, autoLogsPerPage]);

    const handleCodesSortChange = (column) => {
        setCodesPage(1);
        setCodesSort((prev) => nextSortState(prev, column));
    };

    const handleConnectedSortChange = (column) => {
        setConnectedPage(1);
        setConnectedSort((prev) => nextSortState(prev, column));
    };

    const handleNotifySortChange = (column) => {
        setNotifyPage(1);
        setNotifySort((prev) => nextSortState(prev, column));
    };

    const handleCodesPaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== codesPerPage) {
            setCodesPerPage(nextPerPage);
            setCodesPage(1);
            return;
        }
        setCodesPage(nextPage);
    };

    const handleConnectedPaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== connectedPerPage) {
            setConnectedPerPage(nextPerPage);
            setConnectedPage(1);
            return;
        }
        setConnectedPage(nextPage);
    };

    const handleNotifyPaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== notifyPerPage) {
            setNotifyPerPage(nextPerPage);
            setNotifyPage(1);
            return;
        }
        setNotifyPage(nextPage);
    };

    const handleAutoRulesSortChange = (column) => {
        setAutoRulesPage(1);
        setAutoRulesSort((prev) => nextSortState(prev, column));
    };

    const handleAutoRulesPaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== autoRulesPerPage) {
            setAutoRulesPerPage(nextPerPage);
            setAutoRulesPage(1);
            return;
        }
        setAutoRulesPage(nextPage);
    };

    const handleAutoLogsPaginationChange = (nextPage, nextPerPage) => {
        if (nextPerPage !== autoLogsPerPage) {
            setAutoLogsPerPage(nextPerPage);
            setAutoLogsPage(1);
            return;
        }
        setAutoLogsPage(nextPage);
    };

    const renderTableFooter = ({
        selectId,
        rowsLength,
        total,
        currentPage,
        perPage,
        onPaginationChange,
        noun,
    }) => {
        const totalPages = Math.max(1, Math.ceil(total / perPage));
        return (
            <div className={tableStyles.tableFooter}>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
                    <Pagination className={tableStyles.pagination}>
                        <Pagination.Prev
                            disabled={currentPage <= 1}
                            onClick={() => onPaginationChange(currentPage - 1, perPage)}
                        />
                        {getPageNumbers(currentPage, totalPages, (page) => onPaginationChange(page, perPage))}
                        <Pagination.Next
                            disabled={currentPage >= totalPages}
                            onClick={() => onPaginationChange(currentPage + 1, perPage)}
                        />
                    </Pagination>
                    <label htmlFor={selectId} className={tableStyles.perPageLabel}>
                        Page Size:
                    </label>
                    <Form.Control
                        as="select"
                        id={selectId}
                        value={perPage}
                        onChange={(e) => onPaginationChange(1, parseInt(e.target.value, 10))}
                        className={tableStyles.perPage}
                        style={{ width: 80 }}
                    >
                        {[10, 20, 50, 100].map((size) => (
                            <option key={size} value={size}>
                                {size}
                            </option>
                        ))}
                    </Form.Control>
                    <span className={tableStyles.results}>
                        Showing {rowsLength} of {total} {noun}
                    </span>
                </div>
            </div>
        );
    };

    const createCode = async (e) => {
        e.preventDefault();
        const trimmed = label.trim();
        if (!trimmed) {
            setCreateError('Label is required.');
            return;
        }
        setCreating(true);
        setCreateError('');
        try {
            await axios.post(
                `${baseURL}ticket-transfer-codes`,
                { label: trimmed },
                headers
            );
            successAlert('Transfer code created');
            setLabel('');
            setShowCreateModal(false);
            selectTab('generated');
            setCodesPage(1);
            await loadCodes(1, codesPerPage, codesSort);
        } catch (error) {
            setCreateError(getRequestErrorMessage(error, 'Failed to create transfer code.'));
        } finally {
            setCreating(false);
        }
    };

    const openRenameModal = (code) => {
        setEditingCode(code);
        setEditLabel(code.label || '');
        setEditError('');
    };

    const closeRenameModal = () => {
        setEditingCode(null);
        setEditLabel('');
        setEditError('');
    };

    const renameCode = async (e) => {
        e.preventDefault();
        if (!editingCode) return;
        const trimmed = editLabel.trim();
        if (!trimmed) {
            setEditError('Label is required.');
            return;
        }
        setRenaming(true);
        setEditError('');
        try {
            const { data } = await axios.post(
                `${baseURL}ticket-transfer-code-label/${editingCode.id}`,
                { label: trimmed },
                headers
            );
            successAlert('Label updated');
            if (data?.code) {
                setCodes((prev) => prev.map((row) => (
                    row.id === editingCode.id ? { ...row, label: data.code.label } : row
                )));
            } else {
                await loadCodes(codesPage, codesPerPage, codesSort);
            }
            closeRenameModal();
        } catch (error) {
            setEditError(getRequestErrorMessage(error, 'Failed to update label.'));
        } finally {
            setRenaming(false);
        }
    };

    const handleActiveChange = async (code, isActive) => {
        setUpdatingActiveId(code.id);
        try {
            await axios.post(
                `${baseURL}ticket-transfer-code-toggle/${code.id}`,
                { is_active: isActive },
                headers
            );
            successAlert(isActive ? 'Transfer code activated' : 'Transfer code deactivated');
            setCodes((prev) => prev.map((row) => (
                row.id === code.id ? { ...row, is_active: isActive } : row
            )));
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to update status.');
        } finally {
            setUpdatingActiveId(null);
        }
    };

    const handleDelete = (code) => {
        const connectionCount = (code.connections || []).length;
        const workspaceNames = (code.connections || [])
            .map((c) => c.workspace?.name)
            .filter(Boolean)
            .join(', ');

        showConfirmDialog({
            title: 'Delete this transfer code?',
            message: connectionCount > 0
                ? `Warning: ${connectionCount} workspace${connectionCount === 1 ? '' : 's'} connected (${workspaceNames || 'connected workspaces'}). Deleting will revoke their connection and they will no longer be able to transfer tickets to you.`
                : 'You will not be able to recover this code.',
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                try {
                    await axios.post(
                        `${baseURL}ticket-transfer-code-delete/${code.id}`,
                        {},
                        headers
                    );
                    successAlert('Transfer code deleted');
                    await loadCodes(codesPage, codesPerPage, codesSort);
                } catch (error) {
                    failAlert(error?.response?.data?.message ?? 'Failed to delete transfer code.');
                }
            },
        });
    };

    const connect = async (e) => {
        e.preventDefault();
        setConnecting(true);
        setConnectError('');
        try {
            const { data } = await axios.post(
                `${baseURL}ticket-transfer-connect`,
                { uuid: connectUuid.trim() },
                headers
            );
            // successAlert appends " successfully!" — keep the phrase short
            successAlert(
                data?.connection?.code_is_active === false
                    ? 'Connected (transfers paused)'
                    : 'Connected'
            );
            setConnectUuid('');
            setConnectError('');
            setShowConnectModal(false);
            selectTab('connections');
            setConnectedPage(1);
            await loadConnections(1, connectedPerPage, connectedSort);
        } catch (error) {
            const msg =
                error?.response?.data?.errors?.uuid?.[0] ||
                error?.response?.data?.message ||
                'Failed to connect.';
            setConnectError(msg);
        } finally {
            setConnecting(false);
        }
    };

    const closeConnectModal = () => {
        setShowConnectModal(false);
        setConnectUuid('');
        setConnectError('');
    };

    const disconnect = (connection) => {
        showConfirmDialog({
            title: 'Disconnect transfer access?',
            message: `You will no longer be able to transfer tickets to ${connection.owner_workspace?.name || 'that workspace'} using this code.`,
            confirmLabel: 'Disconnect',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                setDisconnectingId(connection.id);
                try {
                    await axios.post(
                        `${baseURL}ticket-transfer-disconnect/${connection.id}`,
                        {},
                        headers
                    );
                    successAlert('Disconnected');
                    await loadConnections(connectedPage, connectedPerPage, connectedSort);
                } catch (error) {
                    failAlert(error?.response?.data?.message ?? 'Failed to disconnect.');
                } finally {
                    setDisconnectingId(null);
                }
            },
        });
    };

    const deauthorize = (code, connection) => {
        const workspaceName = connection.workspace?.name || 'This workspace';
        showConfirmDialog({
            title: 'Deauthorize workspace?',
            message: `${workspaceName} will be removed from this code and will no longer be able to transfer tickets to you.`,
            confirmLabel: 'Deauthorize',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                setDeauthorizingId(connection.id);
                try {
                    await axios.post(
                        `${baseURL}ticket-transfer-deauthorize/${connection.id}`,
                        {},
                        headers
                    );
                    successAlert('Workspace deauthorized');
                    setCodes((prev) => prev.map((row) => {
                        if (row.id !== code.id) return row;
                        return {
                            ...row,
                            connections: (row.connections || []).filter((c) => c.id !== connection.id),
                        };
                    }));
                } catch (error) {
                    failAlert(error?.response?.data?.message ?? 'Failed to deauthorize workspace.');
                } finally {
                    setDeauthorizingId(null);
                }
            },
        });
    };

    const addNotifyEmail = async (e) => {
        e.preventDefault();
        const email = notifyEmailInput.trim().toLowerCase();
        if (!email) {
            failAlert('Please enter an email address.');
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            failAlert('Please enter a valid email with a domain (e.g. name@example.com).');
            return;
        }
        if (notifyEmails.some((row) => (row.email || '').toLowerCase() === email)) {
            failAlert('This email is already on the list.');
            return;
        }

        setNotifySaving(true);
        try {
            await axios.post(
                `${baseURL}ticket-transfer-notification-emails`,
                { email },
                headers
            );
            setNotifyEmailInput('');
            setNotifyPage(1);
            await loadNotifyEmails(1, notifyPerPage, notifySort);
            successAlert('Notification email added');
        } catch (error) {
            failAlert(getRequestErrorMessage(error, 'Failed to add notification email.'));
        } finally {
            setNotifySaving(false);
        }
    };

    const removeNotifyEmail = (row) => {
        showConfirmDialog({
            title: 'Remove notification email?',
            message: `${row.email} will no longer receive emails when tickets are transferred into this workspace.`,
            confirmLabel: 'Remove',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                setNotifyRemovingId(row.id);
                try {
                    await axios.post(
                        `${baseURL}ticket-transfer-notification-email-delete/${row.id}`,
                        {},
                        headers
                    );
                    successAlert('Notification email removed');
                    await loadNotifyEmails(notifyPage, notifyPerPage, notifySort);
                } catch (error) {
                    failAlert(error?.response?.data?.message ?? 'Failed to remove notification email.');
                } finally {
                    setNotifyRemovingId(null);
                }
            },
        });
    };

    const openCreateRuleModal = () => {
        setEditingRule(null);
        setShowRuleModal(true);
    };

    const openEditRuleModal = (rule) => {
        setEditingRule(rule);
        setShowRuleModal(true);
    };

    const handleRuleSaved = () => {
        setAutoRulesPage(1);
        loadAutoRules(1, autoRulesPerPage, autoRulesSort);
    };

    const handleToggleRule = async (rule, nextActive) => {
        setTogglingRuleId(rule.id);
        try {
            await axios.post(
                `${baseURL}auto-transfer-rule-toggle/${rule.id}`,
                { is_active: nextActive },
                headers
            );
            successAlert(nextActive ? 'Rule activated' : 'Rule deactivated');
            setAutoRules((prev) => prev.map((row) => (
                row.id === rule.id ? { ...row, is_active: nextActive } : row
            )));
        } catch (error) {
            failAlert(error?.response?.data?.message ?? 'Failed to update rule status.');
        } finally {
            setTogglingRuleId(null);
        }
    };

    const handleDeleteRule = (rule) => {
        showConfirmDialog({
            title: 'Delete this auto-transfer rule?',
            message: `Tickets matching "${rule.name}" will no longer be transferred automatically.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            onConfirm: async () => {
                try {
                    await axios.post(`${baseURL}auto-transfer-rule-delete/${rule.id}`, {}, headers);
                    successAlert('Auto-transfer rule deleted');
                    await loadAutoRules(autoRulesPage, autoRulesPerPage, autoRulesSort);
                } catch (error) {
                    failAlert(error?.response?.data?.message ?? 'Failed to delete auto-transfer rule.');
                }
            },
        });
    };

    const copyUuid = async (uuid) => {
        try {
            await navigator.clipboard.writeText(uuid);
            successAlert('Code copied to clipboard');
        } catch {
            failAlert('Could not copy code.');
        }
    };

    const renderSkeleton = (cols = 7) => (
        <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
            <Table className={tableStyles.table}>
                <thead>
                    <tr>
                        {Array.from({ length: cols - 1 }).map((_, i) => (
                            <th key={i}>&nbsp;</th>
                        ))}
                        <th className={tableStyles.stickyActions} style={{ width: 110, textAlign: 'center' }}>
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: 5 }).map((_, idx) => (
                        <tr key={idx}>
                            {Array.from({ length: cols - 1 }).map((__, col) => (
                                <td key={col}>
                                    <Placeholder as="div" animation="glow">
                                        <Placeholder xs={8} style={{ display: 'block', borderRadius: 4 }} />
                                    </Placeholder>
                                </td>
                            ))}
                            <td className={tableStyles.stickyActions} style={{ textAlign: 'center' }}>
                                <Placeholder.Button
                                    variant="secondary"
                                    style={{ width: 26, height: 26, padding: 0, borderRadius: 5 }}
                                    aria-hidden="true"
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );

    const generatedCodesTable = (
        <>
            <Row className="pb-3">
                <Col className="d-flex flex-wrap align-items-start gap-2">
                    <p className="text-muted mb-0 me-auto" style={{ fontSize: 14 }}>
                        Codes you generate and share so other workspaces can connect and transfer tickets into this workspace.
                    </p>
                    <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => setShowCreateModal(true)}
                            style={{ whiteSpace: 'nowrap' }}
                            disabled={loading}
                        >
                            <img src={plus} alt="" />
                            <span className="ms-1">Generate Code</span>
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col className="overflow-x-auto">
                    {loading ? (
                        renderSkeleton(7)
                    ) : (
                        <>
                        <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                            <Table className={tableStyles.table} hover>
                                <thead>
                                    <tr>
                                        <EmbedListSortableTh
                                            columnKey="uuid"
                                            label="Code"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="label"
                                            label="Label"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="created_by"
                                            label="Created by"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="created_at"
                                            label="Created"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="connections"
                                            label="Connected workspaces"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="is_active"
                                            label="Status"
                                            sort={codesSort}
                                            onSortChange={handleCodesSortChange}
                                        />
                                        <th
                                            className={tableStyles.stickyActions}
                                            style={{ width: 140, textAlign: 'center' }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {codes.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="text-muted embed-ticket-forms-table__empty">
                                                No generated codes yet. Create one and share it.
                                            </td>
                                        </tr>
                                    ) : (
                                        codes.map((code) => (
                                            <tr key={code.id} className="embed-ticket-forms-table-row">
                                                <td>
                                                    <code style={{ fontSize: 12 }}>{code.uuid}</code>
                                                </td>
                                                <td>
                                                    <span className={tableStyles.title}>{code.label || '—'}</span>
                                                </td>
                                                <td>{code.created_by?.name || '—'}</td>
                                                <td className="text-muted">
                                                    {code.created_at
                                                        ? formatDateTime(code.created_at)
                                                        : '—'}
                                                </td>
                                                <td>
                                                    {(code.connections || []).length === 0 ? (
                                                        <span className="text-muted">None</span>
                                                    ) : (
                                                        <div className="d-flex flex-column gap-1">
                                                            {code.connections.map((c) => (
                                                                <div
                                                                    key={c.id}
                                                                    className="d-inline-flex align-items-center gap-1"
                                                                >
                                                                    <Badge bg="secondary">
                                                                        {c.workspace?.name || 'Workspace'}
                                                                    </Badge>
                                                                    <button
                                                                        type="button"
                                                                        className={tableStyles.actionBtn}
                                                                        title="Deauthorize"
                                                                        aria-label={`Deauthorize ${c.workspace?.name || 'workspace'}`}
                                                                        disabled={deauthorizingId === c.id}
                                                                        onClick={() => deauthorize(code, c)}
                                                                    >
                                                                        <TbPlugConnectedX size={13} aria-hidden />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className={tableStyles.selectCell}>
                                                    <EmbedFormsActiveSelect
                                                        isActive={!!code.is_active}
                                                        disabled={updatingActiveId === code.id}
                                                        onChange={(value) => handleActiveChange(code, value)}
                                                    />
                                                </td>
                                                <td
                                                    className={tableStyles.stickyActions}
                                                    style={{ textAlign: 'center' }}
                                                >
                                                    <div style={{ display: 'inline-flex', gap: 5 }}>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Rename label"
                                                            aria-label="Rename label"
                                                            onClick={() => openRenameModal(code)}
                                                        >
                                                            <BsPencil size={11} aria-hidden />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Copy code"
                                                            aria-label="Copy code"
                                                            onClick={() => copyUuid(code.uuid)}
                                                        >
                                                            <BsClipboard size={11} aria-hidden />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Delete"
                                                            aria-label="Delete"
                                                            onClick={() => handleDelete(code)}
                                                        >
                                                            <BsTrash3 size={11} aria-hidden />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        {renderTableFooter({
                            selectId: 'transferCodesPerPageSelect',
                            rowsLength: codes.length,
                            total: codesMeta.total,
                            currentPage: codesMeta.current_page || codesPage,
                            perPage: codesPerPage,
                            onPaginationChange: handleCodesPaginationChange,
                            noun: 'codes',
                        })}
                        </>
                    )}
                </Col>
            </Row>
        </>
    );

    const connectionsTable = (
        <>
            <Row className="pb-3">
                <Col className="d-flex flex-wrap align-items-start gap-2">
                    <p className="text-muted mb-0 me-auto" style={{ fontSize: 14 }}>
                        Connections you made by entering another workspace’s code. You can transfer tickets into those workspaces while the code is active.
                    </p>
                    <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                        <Button
                            type="button"
                            variant="primary"
                            onClick={() => {
                                setConnectError('');
                                setShowConnectModal(true);
                            }}
                            style={{ whiteSpace: 'nowrap' }}
                            disabled={connectionsLoading}
                        >
                            Connect Code
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col className="overflow-x-auto">
                    {connectionsLoading ? (
                        renderSkeleton(6)
                    ) : (
                        <>
                        <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                            <Table className={tableStyles.table} hover>
                                <thead>
                                    <tr>
                                        <EmbedListSortableTh
                                            columnKey="workspace"
                                            label="Workspace"
                                            sort={connectedSort}
                                            onSortChange={handleConnectedSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="uuid"
                                            label="Their code"
                                            sort={connectedSort}
                                            onSortChange={handleConnectedSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="connected_by"
                                            label="Connected by"
                                            sort={connectedSort}
                                            onSortChange={handleConnectedSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="connected_at"
                                            label="Connected"
                                            sort={connectedSort}
                                            onSortChange={handleConnectedSortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="code_is_active"
                                            label="Their code status"
                                            sort={connectedSort}
                                            onSortChange={handleConnectedSortChange}
                                        />
                                        <th
                                            className={tableStyles.stickyActions}
                                            style={{ width: 110, textAlign: 'center' }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {connected.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-muted embed-ticket-forms-table__empty">
                                                No connections yet. Connect a code from another workspace.
                                            </td>
                                        </tr>
                                    ) : (
                                        connected.map((row) => (
                                            <tr key={row.id} className="embed-ticket-forms-table-row">
                                                <td>
                                                    <span className={tableStyles.title}>
                                                        {row.owner_workspace?.name || '—'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <code style={{ fontSize: 12 }}>{row.uuid}</code>
                                                </td>
                                                <td>{row.connected_by?.name || '—'}</td>
                                                <td className="text-muted">
                                                    {row.connected_at
                                                        ? formatDateTime(row.connected_at)
                                                        : '—'}
                                                </td>
                                                <td>
                                                    <Badge bg={row.code_is_active ? 'success' : 'secondary'}>
                                                        {row.code_is_active ? 'Active' : 'Deactivated'}
                                                    </Badge>
                                                    {!row.code_is_active && (
                                                        <div className="small text-muted mt-1">
                                                            Transfers are paused until they reactivate this code.
                                                        </div>
                                                    )}
                                                </td>
                                                <td
                                                    className={tableStyles.stickyActions}
                                                    style={{ textAlign: 'center' }}
                                                >
                                                    <button
                                                        type="button"
                                                        className={tableStyles.actionBtn}
                                                        title="Disconnect"
                                                        aria-label="Disconnect"
                                                        disabled={disconnectingId === row.id}
                                                        onClick={() => disconnect(row)}
                                                    >
                                                        <TbPlugConnectedX size={13} aria-hidden />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        {renderTableFooter({
                            selectId: 'transferConnectionsPerPageSelect',
                            rowsLength: connected.length,
                            total: connectedMeta.total,
                            currentPage: connectedMeta.current_page || connectedPage,
                            perPage: connectedPerPage,
                            onPaginationChange: handleConnectedPaginationChange,
                            noun: 'connections',
                        })}
                        </>
                    )}
                </Col>
            </Row>
        </>
    );

    const notificationsTable = (
        <>
            <Row className="pb-3">
                <Col className="d-flex flex-wrap align-items-start gap-2">
                    <p className="text-muted mb-0 me-auto text-align-left" style={{ fontSize: 14, textAlign: 'left' }}>
                        When a ticket is transferred into this workspace, everyone listed below receives an email.
                    </p>
                </Col>
            </Row>
            <Row className="pb-3">
                <Col md={8} lg={6}>
                    <Form onSubmit={addNotifyEmail}>
                        <div className="d-flex flex-wrap gap-2">
                            <Form.Control
                                type="email"
                                value={notifyEmailInput}
                                onChange={(e) => setNotifyEmailInput(e.target.value)}
                                placeholder="name@example.com"
                                disabled={notifySaving}
                                style={{ maxWidth: 320 }}
                            />
                            <Button type="submit" variant="primary" disabled={notifySaving || !notifyEmailInput.trim()}>
                                {notifySaving ? 'Adding…' : 'Add email'}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
            <Row>
                <Col className="overflow-x-auto">
                    {notifyLoading ? (
                        renderSkeleton(4)
                    ) : (
                        <>
                        <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                            <Table className={tableStyles.table} hover>
                                <thead>
                                    <tr>
                                        <EmbedListSortableTh
                                            columnKey="email"
                                            label="Email"
                                            sort={notifySort}
                                            onSortChange={handleNotifySortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="created_by"
                                            label="Added by"
                                            sort={notifySort}
                                            onSortChange={handleNotifySortChange}
                                        />
                                        <EmbedListSortableTh
                                            columnKey="created_at"
                                            label="Added"
                                            sort={notifySort}
                                            onSortChange={handleNotifySortChange}
                                        />
                                        <th
                                            className={tableStyles.stickyActions}
                                            style={{ width: 110, textAlign: 'center' }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {notifyEmails.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="text-muted embed-ticket-forms-table__empty">
                                                No notification emails yet. Add people who should be notified of incoming transfers.
                                            </td>
                                        </tr>
                                    ) : (
                                        notifyEmails.map((row) => (
                                            <tr key={row.id} className="embed-ticket-forms-table-row">
                                                <td>
                                                    <span style={{ textTransform: 'none' }}>{row.email}</span>
                                                </td>
                                                <td>{row.created_by?.name || '—'}</td>
                                                <td className="text-muted">
                                                    {row.created_at
                                                        ? formatDateTime(row.created_at)
                                                        : '—'}
                                                </td>
                                                <td
                                                    className={tableStyles.stickyActions}
                                                    style={{ textAlign: 'center' }}
                                                >
                                                    <button
                                                        type="button"
                                                        className={tableStyles.actionBtn}
                                                        title="Remove"
                                                        aria-label={`Remove ${row.email}`}
                                                        disabled={notifyRemovingId === row.id}
                                                        onClick={() => removeNotifyEmail(row)}
                                                    >
                                                        <BsTrash3 size={11} aria-hidden />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        {renderTableFooter({
                            selectId: 'transferNotifyPerPageSelect',
                            rowsLength: notifyEmails.length,
                            total: notifyMeta.total,
                            currentPage: notifyMeta.current_page || notifyPage,
                            perPage: notifyPerPage,
                            onPaginationChange: handleNotifyPaginationChange,
                            noun: 'emails',
                        })}
                        </>
                    )}
                </Col>
            </Row>
        </>
    );

    const autoTransferTab = (
        <>
            <Row className="pb-3">
                <Col className="d-flex flex-wrap align-items-start gap-2">
                    <p className="text-muted mb-0 me-auto" style={{ fontSize: 14 }}>
                        Automatically transfer new tickets to a connected workspace when AI decides their content
                        matches what a rule describes. Click a rule to edit it.
                    </p>
                    <div className="d-flex flex-wrap align-items-start gap-2 tickets-toolbar-actions">
                        <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={() => setShowAutoLogs((prev) => !prev)}
                        >
                            {showAutoLogs ? 'Hide Logs' : 'View Logs'}
                        </Button>
                        <Button
                            type="button"
                            variant="primary"
                            onClick={openCreateRuleModal}
                            style={{ whiteSpace: 'nowrap' }}
                            disabled={autoRulesLoading}
                        >
                            <img src={plus} alt="" />
                            <span className="ms-1">Create Rule</span>
                        </Button>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col className="overflow-x-auto">
                    {autoRulesLoading ? (
                        renderSkeleton(6)
                    ) : (
                        <>
                        <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                            <Table className={tableStyles.table} hover>
                                <thead>
                                    <tr>
                                        <EmbedListSortableTh
                                            columnKey="name"
                                            label="Name"
                                            sort={autoRulesSort}
                                            onSortChange={handleAutoRulesSortChange}
                                        />
                                        <th>Incoming emails</th>
                                        <th>Keywords</th>
                                        <th>Destination</th>
                                        <EmbedListSortableTh
                                            columnKey="is_active"
                                            label="Status"
                                            sort={autoRulesSort}
                                            onSortChange={handleAutoRulesSortChange}
                                        />
                                        <th
                                            className={tableStyles.stickyActions}
                                            style={{ width: 140, textAlign: 'center' }}
                                        >
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {autoRules.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="text-muted embed-ticket-forms-table__empty">
                                                No auto-transfer rules yet. Create one to start routing tickets automatically.
                                            </td>
                                        </tr>
                                    ) : (
                                        autoRules.map((rule) => (
                                            <tr
                                                key={rule.id}
                                                className="embed-ticket-forms-table-row"
                                                style={{ cursor: 'pointer' }}
                                                title="Click to edit this rule"
                                                onClick={() => openEditRuleModal(rule)}
                                            >
                                                <td>
                                                    <span className={tableStyles.title}>{rule.name}</span>
                                                </td>
                                                <td>
                                                    {(rule.emails || []).length === 0 ? (
                                                        <span className="text-muted">All</span>
                                                    ) : (
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {rule.emails.map((email) => (
                                                                <Badge key={email.id} bg="light" text="dark">
                                                                    {email.email_address}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {(rule.keywords || []).map((keyword) => (
                                                            <Badge key={keyword} bg="secondary" style={keywordBadgeStyle}>
                                                                {keyword}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>{rule.destination_workspace?.name || '—'}</td>
                                                {/* Status and Actions carry their own controls, so a click
                                                    there must not also open the row's edit modal. */}
                                                <td
                                                    className={tableStyles.selectCell}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <EmbedFormsActiveSelect
                                                        isActive={!!rule.is_active}
                                                        disabled={togglingRuleId === rule.id}
                                                        onChange={(value) => handleToggleRule(rule, value)}
                                                    />
                                                </td>
                                                <td
                                                    className={tableStyles.stickyActions}
                                                    style={{ textAlign: 'center' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div style={{ display: 'inline-flex', gap: 5 }}>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Test rule"
                                                            aria-label="Test rule"
                                                            onClick={() => setTestingRule(rule)}
                                                        >
                                                            <BsPlayCircle size={11} aria-hidden />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Edit"
                                                            aria-label="Edit"
                                                            onClick={() => openEditRuleModal(rule)}
                                                        >
                                                            <BsPencil size={11} aria-hidden />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={tableStyles.actionBtn}
                                                            title="Delete"
                                                            aria-label="Delete"
                                                            onClick={() => handleDeleteRule(rule)}
                                                        >
                                                            <BsTrash3 size={11} aria-hidden />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        {renderTableFooter({
                            selectId: 'autoTransferRulesPerPageSelect',
                            rowsLength: autoRules.length,
                            total: autoRulesMeta.total,
                            currentPage: autoRulesMeta.current_page || autoRulesPage,
                            perPage: autoRulesPerPage,
                            onPaginationChange: handleAutoRulesPaginationChange,
                            noun: 'rules',
                        })}
                        </>
                    )}
                </Col>
            </Row>

            {showAutoLogs ? (
                <Row className="pt-4">
                    <Col className="overflow-x-auto">
                        <h6 className="mb-1">Recent activity</h6>
                        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                            Click a row to see why the ticket did or did not move.
                        </p>
                        {autoLogsLoading ? (
                            renderSkeleton(5)
                        ) : (
                            <>
                            <div className={`${tableStyles.tableWrapper} embed-ticket-forms-table-wrap`}>
                                <Table className={tableStyles.table} hover>
                                    <thead>
                                        <tr>
                                            <th>Ticket</th>
                                            <th>Rule</th>
                                            <th>Matched keywords</th>
                                            <th>Outcome</th>
                                            <th>When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {autoLogs.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="text-muted embed-ticket-forms-table__empty">
                                                    No auto-transfer activity yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            autoLogs.map((log) => (
                                                <tr
                                                    key={log.id}
                                                    className="embed-ticket-forms-table-row"
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to see the full decision"
                                                    onClick={() => setSelectedLog(log)}
                                                >
                                                    <td>
                                                        {log.ticket
                                                            ? `#${log.ticket.ticket_id || log.ticket.id} — ${log.ticket.title || ''}`
                                                            : '—'}
                                                    </td>
                                                    <td>{log.rule?.name || '—'}</td>
                                                    <td>
                                                        <div className="d-flex flex-wrap gap-1">
                                                            {(log.matched_keywords || []).map((keyword) => (
                                                                <Badge key={keyword} bg="secondary" style={keywordBadgeStyle}>
                                                                    {keyword}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {log.outcome === 'transferred' ? (
                                                            <>
                                                                <Badge bg="success">
                                                                    Transferred{log.to_workspace ? ` → ${log.to_workspace.name}` : ''}
                                                                </Badge>
                                                                {log.ai_invoked ? (
                                                                    <div className="small text-muted mt-1" title={log.ai_reason || ''}>
                                                                        AI confirmed
                                                                    </div>
                                                                ) : null}
                                                            </>
                                                        ) : log.outcome === 'skipped_no_match' ? (
                                                            <Badge bg="secondary">No match</Badge>
                                                        ) : log.outcome === 'skipped_ambiguous_match' ? (
                                                            <Badge bg="warning" text="dark" title={log.error_message || ''}>
                                                                Ambiguous — not sent
                                                            </Badge>
                                                        ) : log.outcome === 'skipped_ai_rejected' ? (
                                                            <Badge bg="info" text="dark" title={log.ai_reason || ''}>
                                                                AI rejected — not sent
                                                            </Badge>
                                                        ) : (
                                                            <Badge bg="danger" title={log.error_message || ''}>
                                                                Error
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-muted">
                                                        {log.matched_at
                                                            ? formatDateTime(log.matched_at)
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                            {renderTableFooter({
                                selectId: 'autoTransferLogsPerPageSelect',
                                rowsLength: autoLogs.length,
                                total: autoLogsMeta.total,
                                currentPage: autoLogsMeta.current_page || autoLogsPage,
                                perPage: autoLogsPerPage,
                                onPaginationChange: handleAutoLogsPaginationChange,
                                noun: 'log entries',
                            })}
                            </>
                        )}
                    </Col>
                </Row>
            ) : null}
        </>
    );

    return (
        <MainContainer>
            <Container fluid className="embed-forms-page">
                <Row className="pt-4 pb-2">
                    <Col className="table-page-header" style={{ justifyContent: 'flex-start' }}>
                        <h5 className="flex-grow-1 m-0 text-align-left">Transfering Codes</h5>
                    </Col>
                </Row>

                <Tabs
                    activeKey={activeTab}
                    onSelect={(key) => selectTab(key || 'generated')}
                    className="mb-3"
                >
                    <Tab eventKey="generated" title="Generated Codes">
                        {generatedCodesTable}
                    </Tab>
                    <Tab eventKey="connections" title="Connections">
                        {connectionsTable}
                    </Tab>
                    <Tab eventKey="notifications" title="Notification Emails">
                        {notificationsTable}
                    </Tab>
                    <Tab eventKey="auto-transfer" title="Auto-transfer">
                        {autoTransferTab}
                    </Tab>
                </Tabs>
            </Container>

            <Modal
                show={showCreateModal}
                onHide={() => {
                    setShowCreateModal(false);
                    setCreateError('');
                    setLabel('');
                }}
                centered
            >
                <Form onSubmit={createCode}>
                    <Modal.Header closeButton>
                        <Modal.Title>Generate transfer code</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>Label</Form.Label>
                            <Form.Control
                                value={label}
                                onChange={(e) => {
                                    setLabel(e.target.value);
                                    if (createError) setCreateError('');
                                }}
                                placeholder="e.g. Partner Agency"
                                maxLength={255}
                                required
                                isInvalid={!!createError}
                            />
                            {createError ? (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {createError}
                                </Form.Control.Feedback>
                            ) : (
                                <Form.Text className="text-muted">
                                    Required and unique in this workspace.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowCreateModal(false);
                                setCreateError('');
                                setLabel('');
                            }}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={creating || !label.trim()}>
                            {creating ? 'Creating…' : 'Generate Code'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={!!editingCode} onHide={closeRenameModal} centered>
                <Form onSubmit={renameCode}>
                    <Modal.Header closeButton>
                        <Modal.Title>Rename transfer code</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted small mb-3">
                            Renaming only changes the label. The code UUID stays the same, so existing connections keep working.
                        </p>
                        {editingCode?.uuid ? (
                            <p className="small mb-3">
                                <code>{editingCode.uuid}</code>
                            </p>
                        ) : null}
                        <Form.Group>
                            <Form.Label>Label</Form.Label>
                            <Form.Control
                                value={editLabel}
                                onChange={(e) => {
                                    setEditLabel(e.target.value);
                                    if (editError) setEditError('');
                                }}
                                placeholder="e.g. Partner Agency"
                                maxLength={255}
                                required
                                isInvalid={!!editError}
                                autoFocus
                            />
                            {editError ? (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {editError}
                                </Form.Control.Feedback>
                            ) : null}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeRenameModal}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={renaming || !editLabel.trim()}>
                            {renaming ? 'Saving…' : 'Save label'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showConnectModal} onHide={closeConnectModal} centered>
                <Form onSubmit={connect}>
                    <Modal.Header closeButton>
                        <Modal.Title>Connect a transfer code</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="text-muted small">
                            Paste a code you received. You will be able to transfer tickets into that workspace while their code is active.
                        </p>
                        <Form.Group>
                            <Form.Label>Code</Form.Label>
                            <Form.Control
                                value={connectUuid}
                                onChange={(e) => {
                                    setConnectUuid(e.target.value);
                                    if (connectError) setConnectError('');
                                }}
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                required
                                isInvalid={!!connectError}
                            />
                            {connectError ? (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                    {connectError}
                                </Form.Control.Feedback>
                            ) : null}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeConnectModal}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={connecting || !connectUuid.trim()}>
                            {connecting ? 'Connecting…' : 'Connect'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <AutoTransferRuleModal
                show={showRuleModal}
                onHide={() => setShowRuleModal(false)}
                rule={editingRule}
                onSaved={handleRuleSaved}
            />

            <AutoTransferTestModal
                show={!!testingRule}
                onHide={() => setTestingRule(null)}
                rule={testingRule}
            />

            <AutoTransferLogModal
                show={!!selectedLog}
                onHide={() => setSelectedLog(null)}
                log={selectedLog}
            />
        </MainContainer>
    );
};

export default TransferConnections;
